import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    Award, Plus, Edit, Trash2, Save, X, 
    CheckCircle, AlertTriangle, Loader2, Globe,
    Layout, Zap, Settings,
    Search, RefreshCw, Sparkles, Brain, ListChecks,
    Beaker, BookOpen, Stethoscope, ChevronRight, Info, Wand2,
    AlertCircle, ListTodo, Headphones, PlayCircle, Timer,
    Activity, Database, TrendingUp, Users, History,
    Shield, Route, Check, Filter, Copy, ArrowRight,
    HelpCircle, Eye, EyeOff, Lock, Unlock, Layers,
    ToggleLeft, ToggleRight, Image as ImageIcon,
    GraduationCap, ShieldCheck, CheckCheck, FileCheck2
} from 'lucide-react';
import { StorageService } from '../../services/storageService';
import { generateCertStrategy, generateCategorySuggestions, verifyClinicalContent, generateCertGatewaySeoPage } from '../../services/geminiService';
import { getDefaultClinicalConfig, DEFAULT_CERT_CLINICAL_CONFIGS, CertificationClinicalConfig, getDefaultGatewaySeo } from '../../services/promptService';
import { CertificationType, CertificationGatewaySeo, GatewayFaqItem } from '../../types';

const DEFAULT_MODULE_CONFIG = {
    dashboard: true,
    podcasts: true,
    studyGuide: true,
    practiceQuiz: true,
    practiceExam: true,
    scenarios: true,
    missedBank: true,
    skillsLab: true,
    mathLab: true,
    history: true,
    aiTutor: true,
    flashcards: true,
    diagnostic: true
};

export interface CertRouteDefinition {
    id: string;
    name: string;
    urlPattern: (certId: string) => string;
    category: 'Practice & Prep' | 'Interactive Labs' | 'Knowledge & Audio' | 'Analytics & Community';
    description: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    isPopularDefault?: boolean;
}

export const ALL_AVAILABLE_CERT_ROUTES: CertRouteDefinition[] = [
    {
        id: 'flashcards',
        name: 'Flashcards & SRS Decks',
        urlPattern: (id) => `/${id}/flashcards`,
        category: 'Practice & Prep',
        description: 'Spaced repetition flashcards with confidence ratings and flip study mode',
        icon: Zap,
        isPopularDefault: true
    },
    {
        id: 'practice',
        name: 'Practice Quiz & Q-Bank',
        urlPattern: (id) => `/${id}/practice`,
        category: 'Practice & Prep',
        description: 'Custom quizzes, clinical distractors, rationales, and question bank',
        icon: ListChecks,
        isPopularDefault: true
    },
    {
        id: 'study-quiz',
        name: 'Study Quiz (Direct)',
        urlPattern: (id) => `/${id}/study-quiz`,
        category: 'Practice & Prep',
        description: 'Instant direct launch quiz route for practice exploration',
        icon: PlayCircle,
        isPopularDefault: true
    },
    {
        id: 'guide',
        name: 'Interactive Study Guide',
        urlPattern: (id) => `/${id}/guide`,
        category: 'Knowledge & Audio',
        description: 'Interactive slide curriculum, clinical key points, and downloadable summaries',
        icon: BookOpen,
        isPopularDefault: true
    },
    {
        id: 'podcasts',
        name: 'Concept Audio Hub (Podcasts)',
        urlPattern: (id) => `/${id}/podcasts`,
        category: 'Knowledge & Audio',
        description: 'High-yield audio episodes with timestamps and key takeaway takeaways',
        icon: Headphones,
        isPopularDefault: false
    },
    {
        id: 'skills',
        name: 'Clinical Skills Lab',
        urlPattern: (id) => `/${id}/skills`,
        category: 'Interactive Labs',
        description: 'Clinical step-by-step procedures, safety critical criteria, and checklists',
        icon: Stethoscope,
        isPopularDefault: false
    },
    {
        id: 'simulator',
        name: 'Clinical Scenario Simulator',
        urlPattern: (id) => `/${id}/simulator`,
        category: 'Interactive Labs',
        description: 'Branching clinical decision scenarios with patient vitals and outcomes',
        icon: Beaker,
        isPopularDefault: false
    },
    {
        id: 'exam',
        name: 'Timed Practice Exam',
        urlPattern: (id) => `/${id}/exam`,
        category: 'Practice & Prep',
        description: 'Full-length timed board simulation with weighted scoring and pass/fail prediction',
        icon: Timer,
        isPopularDefault: false
    },
    {
        id: 'scenario',
        name: 'Scenario Challenge Browser',
        urlPattern: (id) => `/${id}/scenario`,
        category: 'Interactive Labs',
        description: 'Clinical scenario selector and case study challenges',
        icon: Activity,
        isPopularDefault: false
    },
    {
        id: 'missed',
        name: 'Missed Question Bank',
        urlPattern: (id) => `/${id}/missed`,
        category: 'Practice & Prep',
        description: 'Remediation pool for questions previously answered incorrectly',
        icon: Database,
        isPopularDefault: false
    },
    {
        id: 'dashboard',
        name: 'Certification Dashboard',
        urlPattern: (id) => `/${id}/dashboard`,
        category: 'Analytics & Community',
        description: 'Main candidate overview, module access points, and readiness score',
        icon: Layout,
        isPopularDefault: false
    },
    {
        id: 'search',
        name: 'Study Hub AI Search',
        urlPattern: (id) => `/${id}/search`,
        category: 'Knowledge & Audio',
        description: 'Universal keyword search across all slides, questions, flashcards, and tracks',
        icon: Search,
        isPopularDefault: false
    },
    {
        id: 'tutor',
        name: 'AI Clinical Tutor',
        urlPattern: (id) => `/${id}/tutor`,
        category: 'Practice & Prep',
        description: 'Interactive AI nursing instructor for questions and clinical concepts',
        icon: Brain,
        isPopularDefault: false
    },
    {
        id: 'analytics',
        name: 'Readiness Analytics',
        urlPattern: (id) => `/${id}/analytics`,
        category: 'Analytics & Community',
        description: 'Candidate readiness charts, mastery trends, and domain performance',
        icon: TrendingUp,
        isPopularDefault: false
    },
    {
        id: 'faculty',
        name: 'Clinical Faculty & Advisors',
        urlPattern: (id) => `/${id}/faculty`,
        category: 'Analytics & Community',
        description: 'Directory of clinical professors, mentors, and program coordinators',
        icon: Users,
        isPopularDefault: false
    },
    {
        id: 'history',
        name: 'Exam & Quiz History',
        urlPattern: (id) => `/${id}/history`,
        category: 'Analytics & Community',
        description: 'Past session logs, score records, and answer reviews',
        icon: History,
        isPopularDefault: false
    }
];

export const CertificationManager: React.FC = () => {
    const [certs, setCerts] = useState<CertificationType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [currentCert, setCurrentCert] = useState<Partial<CertificationType> | null>(null);
    const [status, setStatus] = useState<{type: 'error' | 'success', text: string} | null>(null);
    
    // AI Blueprint State
    const [aiStrategy, setAiStrategy] = useState<any | null>(null);
    const [isBlueprinting, setIsBlueprinting] = useState(false);
    const [isSuggestingCategories, setIsSuggestingCategories] = useState(false);
    const [activeModalTab, setActiveModalTab] = useState<'config' | 'clinical' | 'guest' | 'gateway' | 'seo_details' | 'categories' | 'strategy'>('config');
    const [isTestingClinicalAudit, setIsTestingClinicalAudit] = useState(false);
    const [clinicalAuditTestResult, setClinicalAuditTestResult] = useState<any | null>(null);

    // SEO Details Page Generation State
    const [isGeneratingSeoPage, setIsGeneratingSeoPage] = useState(false);
    const [seoGenerateSuccess, setSeoGenerateSuccess] = useState<string | null>(null);
    const [newKeywordInput, setNewKeywordInput] = useState('');
    const [newWhoForInput, setNewWhoForInput] = useState('');
    const [newDutyInput, setNewDutyInput] = useState('');
    const [newCareerInput, setNewCareerInput] = useState('');
    const [newTipInput, setNewTipInput] = useState('');

    // Route management state in modal
    const [routeCategoryFilter, setRouteCategoryFilter] = useState<string>('all');
    const [routeSearchQuery, setRouteSearchQuery] = useState<string>('');
    const [newCustomRouteInput, setNewCustomRouteInput] = useState<string>('');
    const [isBatchApplying, setIsBatchApplying] = useState<boolean>(false);

    const loadCerts = async () => {
        setIsLoading(true);
        try {
            const data = await StorageService.getCertifications(true);
            setCerts(data);
        } catch (e: any) {
            console.error(e);
            setStatus({ type: 'error', text: 'Registry unreachable: ' + e.message });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { loadCerts(); }, []);

    const handleAdd = () => {
        const initialClinical = getDefaultClinicalConfig('', '', 'Nursing');
        const initialSeo = getDefaultGatewaySeo('', '', '', 'Nursing');
        setCurrentCert({
            id: '',
            name: '',
            shortName: '',
            icon: 'Award',
            isActive: true,
            categories: [],
            modules: { ...DEFAULT_MODULE_CONFIG },
            showOnGateway: true,
            isUpcoming: false,
            gatewayDescription: '',
            gatewayCategory: 'Nursing',
            gatewayImageUrl: '',
            gatewaySeo: initialSeo,
            isGuestTrialEnabled: true,
            guestAllowedRoutes: ['flashcards', 'practice', 'study-quiz', 'exam'],
            customGuestRoutes: [],
            guestQuestionLimit: 50,
            guestExamLimit: 1,
            clinicalConfig: initialClinical
        });
        setAiStrategy(null);
        setClinicalAuditTestResult(null);
        setSeoGenerateSuccess(null);
        setActiveModalTab('config');
        setStatus(null);
        setIsEditing(true);
    };

    const handleEdit = (cert: CertificationType) => {
        const clinicalConfig = cert.clinicalConfig && cert.clinicalConfig.educatorPersona
            ? cert.clinicalConfig
            : getDefaultClinicalConfig(cert.id, cert.name, cert.gatewayCategory);

        const gatewaySeo = cert.gatewaySeo && cert.gatewaySeo.roleOverview
            ? cert.gatewaySeo
            : getDefaultGatewaySeo(cert.id, cert.name, cert.shortName, cert.gatewayCategory);

        // Ensure missing modules and guest fields are initialized cleanly
        setCurrentCert({ 
            ...cert, 
            clinicalConfig,
            gatewaySeo,
            categories: cert.categories || [],
            modules: { ...DEFAULT_MODULE_CONFIG, ...(cert.modules || {}) },
            showOnGateway: cert.showOnGateway !== undefined ? cert.showOnGateway : true,
            isUpcoming: cert.isUpcoming !== undefined ? cert.isUpcoming : false,
            gatewayDescription: cert.gatewayDescription || '',
            gatewayCategory: cert.gatewayCategory || 'Nursing',
            gatewayImageUrl: cert.gatewayImageUrl || '',
            isGuestTrialEnabled: cert.isGuestTrialEnabled !== undefined ? cert.isGuestTrialEnabled : true,
            guestAllowedRoutes: cert.guestAllowedRoutes && cert.guestAllowedRoutes.length > 0 
                ? cert.guestAllowedRoutes 
                : ['flashcards', 'practice', 'study-quiz', 'exam'],
            customGuestRoutes: cert.customGuestRoutes || [],
            guestQuestionLimit: cert.guestQuestionLimit || 50,
            guestExamLimit: cert.guestExamLimit !== undefined ? cert.guestExamLimit : 1
        });
        setAiStrategy(null);
        setClinicalAuditTestResult(null);
        setSeoGenerateSuccess(null);
        setActiveModalTab('config');
        setStatus(null);
        setIsEditing(true);
    };

    const handleAutoPopulateClinical = () => {
        if (!currentCert) return;
        const autoConfig = getDefaultClinicalConfig(currentCert.id, currentCert.name, currentCert.gatewayCategory);
        setCurrentCert(prev => prev ? ({
            ...prev,
            clinicalConfig: autoConfig
        }) : null);
        setStatus({ type: 'success', text: 'Doctoral Clinical Council standards bootstrapped for ' + (currentCert.name || 'this certification') });
    };

    const handleRunClinicalAuditTest = async () => {
        if (!currentCert) return;
        setIsTestingClinicalAudit(true);
        setClinicalAuditTestResult(null);
        try {
            const subject = currentCert.clinicalConfig?.subjectRole || 'resident';
            const sampleItem = {
                text: `During clinical morning care for a ${subject} with acute respiratory compromise, vital signs reveal an SpO2 of 85% and respiratory rate of 32/min. What is the immediate legal priority action under governing board standards?`,
                options: [
                    "Immediately reposition the " + subject + " into High Fowler's position and alert the supervising registered nurse without leaving the room.",
                    "Administer 4 L/min supplemental oxygen via nasal cannula without a physician order.",
                    "Leave the " + subject + " alone in the room to search for a transport wheelchair.",
                    "Encourage high-volume oral fluid intake to relieve airway dryness."
                ],
                explanation: "Elevating the head of the bed (High Fowler's) optimizes diaphragmatic excursion and improves lung ventilation. Under delegated clinical practice acts, unauthorized medication/oxygen administration constitutes a major scope-of-practice violation. Immediate supervising nurse notification is a critical, non-negotiable patient safety checkpoint."
            };

            const auditRes = await verifyClinicalContent(sampleItem, 'quiz', currentCert);
            setClinicalAuditTestResult(auditRes);
            setStatus({ type: 'success', text: 'Clinical audit verification complete. Rigor Score: ' + auditRes.overallHealthScore + '%' });
        } catch (err: any) {
            setStatus({ type: 'error', text: 'Clinical audit test failed: ' + err.message });
        } finally {
            setIsTestingClinicalAudit(false);
        }
    };

    const handleSave = async () => {
        if (!currentCert || !currentCert.id || !currentCert.name) {
            setStatus({ type: 'error', text: 'Identity violation: All identifier fields are mandatory.' });
            return;
        }
        
        setIsSaving(true);
        setStatus(null);
        try {
            const certToSave = {
                ...currentCert,
                gatewaySeo: currentCert.gatewaySeo && currentCert.gatewaySeo.roleOverview
                    ? currentCert.gatewaySeo
                    : getDefaultGatewaySeo(currentCert.id, currentCert.name, currentCert.shortName, currentCert.gatewayCategory),
                clinicalConfig: currentCert.clinicalConfig && currentCert.clinicalConfig.educatorPersona
                    ? currentCert.clinicalConfig
                    : getDefaultClinicalConfig(currentCert.id, currentCert.name, currentCert.gatewayCategory)
            };
            await StorageService.saveCertification(certToSave);

            // Trigger background SEO rebuild
            try {
                await StorageService.rebuildSitemap();
            } catch (syncErr) {
                console.warn('Sitemap rebuild background sync:', syncErr);
            }

            setStatus({ type: 'success', text: 'Registry synchronized: ' + (currentCert.shortName || currentCert.name) + ' updated.' });
            await loadCerts();
            setTimeout(() => setIsEditing(false), 800);
        } catch (e: any) {
            setStatus({ type: 'error', text: 'Synchronization protocol failure: ' + e.message });
        } finally {
            setIsSaving(false);
        }
    };

    const handleOneClickGenerateAndLaunchSeoPage = async () => {
        if (!currentCert || !currentCert.id || !currentCert.name) {
            setStatus({ type: 'error', text: 'Please configure the certificate ID and Name first.' });
            return;
        }
        setIsGeneratingSeoPage(true);
        setSeoGenerateSuccess(null);
        try {
            const generated = await generateCertGatewaySeoPage(currentCert);
            const updatedSeo: CertificationGatewaySeo = {
                ...generated,
                isPublished: true
            };
            const updatedCert: CertificationType = {
                ...currentCert,
                gatewaySeo: updatedSeo
            };
            setCurrentCert(updatedCert);
            await StorageService.saveCertification(updatedCert);

            try {
                await StorageService.rebuildSitemap();
            } catch (syncErr) {
                console.warn('Sitemap rebuild request note:', syncErr);
            }

            setSeoGenerateSuccess(`Public details page synthesized and launched! sitemap.xml & llms.txt refreshed.`);
            await loadCerts();
        } catch (e: any) {
            console.error(e);
            setStatus({ type: 'error', text: 'AI synthesis failed: ' + (e.message || 'Please check API key') });
        } finally {
            setIsGeneratingSeoPage(false);
        }
    };

    const updateGatewaySeo = (field: keyof CertificationGatewaySeo, value: any) => {
        setCurrentCert(prev => {
            if (!prev) return null;
            const currentSeo = prev.gatewaySeo || getDefaultGatewaySeo(prev.id, prev.name, prev.shortName, prev.gatewayCategory);
            return {
                ...prev,
                gatewaySeo: {
                    ...currentSeo,
                    [field]: value
                }
            };
        });
    };

    const updateExamSummary = (field: string, value: any) => {
        setCurrentCert(prev => {
            if (!prev) return null;
            const currentSeo = prev.gatewaySeo || getDefaultGatewaySeo(prev.id, prev.name, prev.shortName, prev.gatewayCategory);
            return {
                ...prev,
                gatewaySeo: {
                    ...currentSeo,
                    examSummary: {
                        ...(currentSeo.examSummary || {
                            formatType: 'CBT Multiple-Choice + Skills',
                            totalQuestions: '70 Questions',
                            timeAllotted: '90 Minutes',
                            passingScore: '70% Minimum Scaled Score',
                            skillsTestDescription: 'Demonstration of clinical procedures under direct evaluative observation.'
                        }),
                        [field]: value
                    }
                }
            };
        });
    };

    const addFaqItem = () => {
        setCurrentCert(prev => {
            if (!prev) return null;
            const currentSeo = prev.gatewaySeo || getDefaultGatewaySeo(prev.id, prev.name, prev.shortName, prev.gatewayCategory);
            const faqs = [...(currentSeo.faqs || [])];
            faqs.push({
                question: 'What are the passing score requirements?',
                answer: 'Candidates must achieve a passing score on the written cognitive assessment as well as successful demonstration of hands-on clinical skills.'
            });
            return {
                ...prev,
                gatewaySeo: {
                    ...currentSeo,
                    faqs
                }
            };
        });
    };

    const updateFaqItem = (index: number, field: keyof GatewayFaqItem, value: string) => {
        setCurrentCert(prev => {
            if (!prev) return null;
            const currentSeo = prev.gatewaySeo || getDefaultGatewaySeo(prev.id, prev.name, prev.shortName, prev.gatewayCategory);
            const faqs = [...(currentSeo.faqs || [])];
            if (faqs[index]) {
                faqs[index] = { ...faqs[index], [field]: value };
            }
            return {
                ...prev,
                gatewaySeo: {
                    ...currentSeo,
                    faqs
                }
            };
        });
    };

    const removeFaqItem = (index: number) => {
        setCurrentCert(prev => {
            if (!prev) return null;
            const currentSeo = prev.gatewaySeo || getDefaultGatewaySeo(prev.id, prev.name, prev.shortName, prev.gatewayCategory);
            const faqs = (currentSeo.faqs || []).filter((_, i) => i !== index);
            return {
                ...prev,
                gatewaySeo: {
                    ...currentSeo,
                    faqs
                }
            };
        });
    };

    const updateClinicalConfig = (field: keyof CertificationClinicalConfig, value: any) => {
        setCurrentCert(prev => {
            if (!prev) return null;
            const base = prev.clinicalConfig || getDefaultClinicalConfig(prev.id, prev.name, prev.gatewayCategory);
            return {
                ...prev,
                clinicalConfig: {
                    ...base,
                    [field]: value
                }
            };
        });
    };

    const handleToggleGuestRoute = (routeId: string) => {
        if (!currentCert) return;
        const currentRoutes = currentCert.guestAllowedRoutes || ['flashcards', 'practice', 'study-quiz', 'exam'];
        let updatedRoutes: string[];
        if (currentRoutes.includes(routeId)) {
            updatedRoutes = currentRoutes.filter(r => r !== routeId);
        } else {
            updatedRoutes = [...currentRoutes, routeId];
        }
        setCurrentCert({ ...currentCert, guestAllowedRoutes: updatedRoutes });
    };

    const handleApplyPreset = (presetType: 'all' | 'core' | 'knowledge' | 'flashcards_only' | 'none') => {
        if (!currentCert) return;
        let routes: string[] = [];
        if (presetType === 'all') {
            routes = ALL_AVAILABLE_CERT_ROUTES.map(r => r.id);
        } else if (presetType === 'core') {
            routes = ['flashcards', 'practice', 'study-quiz', 'exam'];
        } else if (presetType === 'knowledge') {
            routes = ['flashcards', 'practice', 'study-quiz', 'exam', 'guide', 'podcasts', 'skills', 'simulator'];
        } else if (presetType === 'flashcards_only') {
            routes = ['flashcards'];
        } else if (presetType === 'none') {
            routes = [];
        }
        setCurrentCert({
            ...currentCert,
            isGuestTrialEnabled: presetType !== 'none',
            guestAllowedRoutes: routes
        });
    };

    const handleAddCustomRoute = () => {
        if (!newCustomRouteInput.trim() || !currentCert) return;
        let clean = String(newCustomRouteInput || '').trim().toLowerCase().replace(/^\/+/, '');
        const certPrefix = String(currentCert.id || '').toLowerCase() + '/';
        if (clean.startsWith(certPrefix)) {
            clean = clean.slice(certPrefix.length);
        }
        clean = clean.replace(/^\/+/, '');
        const currentCustom = currentCert.customGuestRoutes || [];
        if (clean && !currentCustom.includes(clean)) {
            setCurrentCert({
                ...currentCert,
                customGuestRoutes: [...currentCustom, clean]
            });
        }
        setNewCustomRouteInput('');
    };

    const handleRemoveCustomRoute = (routePattern: string) => {
        if (!currentCert) return;
        setCurrentCert({
            ...currentCert,
            customGuestRoutes: (currentCert.customGuestRoutes || []).filter(r => r !== routePattern)
        });
    };

    const handleBatchApplyGuestPolicy = async () => {
        if (!currentCert || !certs.length) return;
        if (!window.confirm(`Apply this guest route policy (${currentCert.guestAllowedRoutes?.length || 0} routes) to ALL ${certs.length} active certifications?`)) {
            return;
        }

        setIsBatchApplying(true);
        try {
            const updatedCerts = certs.map(c => ({
                ...c,
                isGuestTrialEnabled: currentCert.isGuestTrialEnabled !== undefined ? currentCert.isGuestTrialEnabled : true,
                guestAllowedRoutes: [...(currentCert.guestAllowedRoutes || ['flashcards', 'practice', 'study-quiz'])],
                customGuestRoutes: [...(currentCert.customGuestRoutes || [])],
                guestQuestionLimit: currentCert.guestQuestionLimit || 50,
                guestExamLimit: currentCert.guestExamLimit !== undefined ? currentCert.guestExamLimit : 1
            }));

            await StorageService.saveCertificationsBulk(updatedCerts);
            setStatus({ type: 'success', text: `Successfully updated guest route access across all ${certs.length} certifications!` });
            await loadCerts();
        } catch (e: any) {
            setStatus({ type: 'error', text: 'Batch update failed: ' + e.message });
        } finally {
            setIsBatchApplying(false);
        }
    };

    const handleBlueprint = async () => {
        const cleanName = String(currentCert?.name || '').trim();
        if (!cleanName) {
            setStatus({ type: 'error', text: 'Logic Error: Enter a display name to prime the AI architect.' });
            return;
        }

        setIsBlueprinting(true);
        setStatus(null);
        try {
            const category = currentCert?.gatewayCategory || 'Healthcare';
            const categories = Array.isArray(currentCert?.categories) ? currentCert.categories : [];
            const blueprint = await generateCertStrategy(
                cleanName, 
                category, 
                categories
            );
            setAiStrategy(blueprint);
            
            // Auto-enrich categories if current list is sparse
            const suggestedRaw = blueprint?.categories || blueprint?.clinicalDomains || [];
            const suggestedCategories = Array.isArray(suggestedRaw)
                ? suggestedRaw.map((c: any) => typeof c === 'string' ? c : (c?.name || String(c || ''))).filter(Boolean)
                : [];

            if ((!currentCert?.categories || currentCert.categories.length === 0) && suggestedCategories.length > 0) {
                setCurrentCert(prev => prev ? ({
                    ...prev,
                    categories: suggestedCategories
                }) : null);
            }
            
            setActiveModalTab('strategy');
            setStatus({ type: 'success', text: 'AI Strategy generated successfully!' });
        } catch (e: any) {
            setStatus({ type: 'error', text: 'Architectural derivation failed: ' + (e?.message || String(e)) });
        } finally {
            setIsBlueprinting(false);
        }
    };

    const handleAiSuggestCategories = async () => {
        if (!currentCert?.name) return;
        setIsSuggestingCategories(true);
        try {
            const suggestions = await generateCategorySuggestions(currentCert.name);
            const existing = currentCert.categories || [];
            const combined = Array.from(new Set([...existing, ...suggestions]));
            setCurrentCert({ ...currentCert, categories: combined });
            setStatus({ type: 'success', text: 'AI suggested categories integrated into the registry.' });
        } catch (e: any) {
            setStatus({ type: 'error', text: 'AI Category suggestion failed: ' + e.message });
        } finally {
            setIsSuggestingCategories(false);
        }
    };

    const addCategory = () => {
        const cat = prompt("Enter new category name:");
        if (cat && cat.trim()) {
            const current = currentCert?.categories || [];
            if (!current.includes(cat.trim())) {
                setCurrentCert({ ...currentCert!, categories: [...current, cat.trim()] });
            }
        }
    };

    const removeCategory = (cat: string) => {
        const current = currentCert?.categories || [];
        setCurrentCert({ ...currentCert!, categories: current.filter(c => c !== cat) });
    };

    const toggleModule = (key: keyof CertificationType['modules']) => {
        if (!currentCert?.modules) return;
        setCurrentCert({
            ...currentCert,
            modules: {
                ...currentCert.modules,
                [key]: !currentCert.modules[key]
            }
        });
    };

    const filteredRoutes = ALL_AVAILABLE_CERT_ROUTES.filter(r => {
        const matchesCategory = routeCategoryFilter === 'all' || r.category === routeCategoryFilter;
        const q = String(routeSearchQuery || '').toLowerCase();
        const matchesSearch = !q || 
            String(r.name || '').toLowerCase().includes(q) ||
            String(r.id || '').toLowerCase().includes(q) ||
            String(r.description || '').toLowerCase().includes(q);
        return matchesCategory && matchesSearch;
    });

    const activeGuestRoutesCount = (currentCert?.guestAllowedRoutes || []).length;

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8 animate-fade-in font-sans text-slate-800">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-8 rounded-[2.5rem] border border-slate-200/80 shadow-sm">
                <div className="flex items-center gap-4">
                    <div className="p-4 bg-indigo-600 text-white rounded-3xl shadow-xl shadow-indigo-600/20">
                        <Award size={32} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-slate-900 tracking-tight">Certification & Route Registry</h1>
                        <p className="text-xs text-slate-500 font-bold tracking-widest mt-0.5">
                            Manage exam certification parameters, AI modules, and dynamic guest route permissions
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={loadCerts}
                        className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-2xl border border-slate-200 transition-all"
                        title="Reload registry"
                    >
                        <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
                    </button>
                    <button 
                        onClick={handleAdd}
                        className="px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs tracking-widest transition-all shadow-lg shadow-indigo-600/20 flex items-center gap-2"
                    >
                        <Plus size={18} /> Add Certification
                    </button>
                </div>
            </div>

            {/* Certifications Grid */}
            {isLoading ? (
                <div className="p-20 text-center flex flex-col items-center justify-center space-y-4">
                    <Loader2 className="animate-spin text-indigo-600" size={36} />
                    <p className="text-xs font-black tracking-widest text-slate-400">Loading certification registry...</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {certs.map(cert => {
                        const guestRoutes = cert.guestAllowedRoutes || ['flashcards', 'practice', 'study-quiz'];
                        const isGuestEnabled = cert.isGuestTrialEnabled !== false;

                        return (
                            <div key={cert.id} className="bg-white p-6 rounded-[2.5rem] border border-slate-200/80 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-6">
                                <div className="space-y-4">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center font-black text-indigo-600 text-sm">
                                                {cert.shortName || cert.id.toUpperCase().slice(0, 3)}
                                            </div>
                                            <div>
                                                <h3 className="font-black text-slate-900 text-base leading-snug">{cert.name}</h3>
                                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                    <span className={`text-[8px] font-black px-2 py-0.5 rounded-md ${cert.isActive ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
                                                        {cert.isActive ? 'Active' : 'Offline'}
                                                    </span>
                                                    <span className="text-[8px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md">
                                                        /{cert.id}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => handleEdit(cert)}
                                            className="p-2.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                                            title="Edit certification and guest routes"
                                        >
                                            <Edit size={18}/>
                                        </button>
                                    </div>

                                    {/* Guest Route Access Status Badge */}
                                    <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
                                        <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-wider">
                                            <span className="flex items-center gap-1.5 text-slate-600">
                                                <Shield size={13} className={isGuestEnabled ? 'text-indigo-600' : 'text-slate-400'} />
                                                Guest Mode Route Control
                                            </span>
                                            <span className={`px-2 py-0.5 rounded ${isGuestEnabled ? 'bg-indigo-100 text-indigo-700 font-bold' : 'bg-amber-100 text-amber-800'}`}>
                                                {isGuestEnabled ? `${guestRoutes.length} Routes Open` : 'Login Required'}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-1 pt-0.5">
                                            {isGuestEnabled ? (
                                                guestRoutes.slice(0, 4).map(r => (
                                                    <span key={r} className="text-[8px] font-bold px-1.5 py-0.5 bg-white border border-slate-200 text-indigo-600 rounded font-mono">
                                                        /{r}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-[8px] text-slate-400 font-medium">All routes require verified student account</span>
                                            )}
                                            {isGuestEnabled && guestRoutes.length > 4 && (
                                                <span className="text-[8px] font-bold px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded">
                                                    +{guestRoutes.length - 4} more
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Clinical Council Authority Indicator */}
                                    <div className="flex items-center justify-between p-3 bg-indigo-50/50 rounded-2xl border border-indigo-100/60 text-[9px]">
                                        <div className="flex items-center gap-2 text-indigo-900 font-bold truncate">
                                            <GraduationCap size={13} className="text-indigo-600 shrink-0" />
                                            <span className="truncate">{cert.clinicalConfig?.governingBoard || 'Governing Board Configured'}</span>
                                        </div>
                                        <span className="text-[8px] font-black uppercase tracking-wider text-indigo-700 bg-white px-2 py-0.5 rounded-full border border-indigo-100 shrink-0">
                                            Tier-3 Rigor
                                        </span>
                                    </div>
                                </div>

                                <div className="space-y-2 pt-4 border-t border-slate-100">
                                    <div className="flex justify-between items-center text-[10px] font-black text-slate-400 tracking-widest uppercase">
                                        <span>Active Modules</span>
                                        <span>{Object.values(cert.modules || DEFAULT_MODULE_CONFIG).filter(Boolean).length} Enabled</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {Object.entries(cert.modules || DEFAULT_MODULE_CONFIG).map(([name, enabled]) => (
                                            enabled && <span key={name} className="px-2 py-0.5 bg-slate-100 rounded text-[8px] font-bold text-slate-600">{name}</span>
                                        ))}
                                    </div>
                                    <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
                                        <a 
                                            href={`/programs/${cert.id}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="text-[10px] font-black text-indigo-600 hover:text-indigo-800 flex items-center gap-1 uppercase tracking-wider"
                                            title="Open public SEO details & guide page"
                                        >
                                            <Globe size={12} />
                                            <span>Guide Page</span>
                                            <ArrowRight size={10} />
                                        </a>
                                        <Link 
                                            to={`/manage/admin-config?certId=${cert.id}`}
                                            className="text-[10px] font-black text-emerald-700 hover:text-emerald-900 flex items-center gap-1 uppercase tracking-wider transition-colors"
                                            title="Configure jurisdictional board exam parameters & 1-click 50-state setup"
                                        >
                                            <Sparkles size={11} className="text-amber-500" />
                                            <span>50 States Setup</span>
                                        </Link>
                                        <button 
                                            onClick={() => {
                                                handleEdit(cert);
                                                setActiveModalTab('seo_details');
                                            }}
                                            className="text-[10px] font-black text-slate-500 hover:text-indigo-600 flex items-center gap-1 uppercase tracking-wider transition-colors cursor-pointer"
                                            title="Configure SEO metadata, FAQs, and 1-click AI synthesis"
                                        >
                                            <Wand2 size={11} className="text-indigo-600" />
                                            <span>Manage SEO & AI</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal Dialog */}
            {isEditing && currentCert && (
                <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-xl flex items-start justify-center p-4 pt-8 md:pt-12 overflow-y-auto custom-scrollbar">
                    <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-5xl h-auto max-h-[92vh] flex flex-col animate-pop overflow-hidden">
                        {/* Modal Header */}
                        <div className="p-6 md:p-8 border-b border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-50/50 shrink-0">
                            <div className="flex items-center gap-4 flex-wrap">
                                <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                                    <Award size={24} className="text-indigo-600" />
                                    {currentCert._id ? `Edit ${currentCert.shortName || currentCert.name}` : 'New Certification Logic'}
                                </h3>
                                <div className="flex bg-slate-200/70 p-1 rounded-2xl flex-wrap gap-1">
                                    {[
                                        { id: 'config', label: 'Config', icon: Settings },
                                        { id: 'clinical', label: 'Clinical Council & Board', icon: GraduationCap },
                                        { id: 'guest', label: `Guest Routes (${activeGuestRoutesCount})`, icon: Shield },
                                        { id: 'gateway', label: 'Gateway Display', icon: Globe },
                                        { id: 'seo_details', label: 'SEO & Details Page', icon: Sparkles },
                                        { id: 'categories', label: 'Categories', icon: ListChecks },
                                        { id: 'strategy', label: 'AI Strategy', icon: Brain }
                                    ].map(tab => {
                                        const IconComp = tab.icon;
                                        return (
                                            <button 
                                                key={tab.id}
                                                onClick={() => setActiveModalTab(tab.id as any)}
                                                className={`px-3.5 py-2 rounded-xl text-[10px] font-black tracking-wider transition-all flex items-center gap-1.5 ${
                                                    activeModalTab === tab.id 
                                                        ? 'bg-white text-indigo-600 shadow-sm' 
                                                        : 'text-slate-500 hover:text-slate-900'
                                                }`}
                                            >
                                                <IconComp size={12} />
                                                {tab.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <button onClick={() => setIsEditing(false)} className="p-2 text-slate-400 hover:text-rose-500 rounded-xl transition-colors">
                                <X size={24} />
                            </button>
                        </div>
                        
                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-6 md:p-10 custom-scrollbar space-y-8">
                            {status && (
                                <div className={`p-4 rounded-2xl flex items-center justify-between animate-pop border-2 ${status.type === 'error' ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                                    <div className="flex items-center gap-3 ml-2">
                                        {status.type === 'error' ? <AlertCircle size={18}/> : <CheckCircle size={18}/>}
                                        <span className="font-black text-xs tracking-widest">{status.text}</span>
                                    </div>
                                    <button onClick={() => setStatus(null)} className="p-1 hover:bg-black/5 rounded-lg"><X size={14}/></button>
                                </div>
                            )}

                            {/* TAB 1: Config */}
                            {activeModalTab === 'config' && (
                                <div className="space-y-10">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-6">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 uppercase">Unique Identifier (Routing ID)</label>
                                                <input 
                                                    type="text" 
                                                    value={currentCert.id}
                                                    disabled={!!currentCert._id}
                                                    onChange={(e) => setCurrentCert({ ...currentCert, id: String(e.target.value || '').toLowerCase().replace(/[^a-z0-9]/g, '') })}
                                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-black text-lg focus:bg-white transition-all shadow-inner disabled:opacity-50"
                                                    placeholder="e.g., ekgtech"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 uppercase">Full Display Name</label>
                                                    <button 
                                                        onClick={handleBlueprint}
                                                        disabled={isBlueprinting}
                                                        className="text-[9px] font-black text-indigo-600 flex items-center gap-1 hover:underline"
                                                    >
                                                        {isBlueprinting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12}/>}
                                                        Blueprinter AI
                                                    </button>
                                                </div>
                                                <input 
                                                    type="text" 
                                                    value={currentCert.name}
                                                    onChange={(e) => setCurrentCert({ ...currentCert, name: e.target.value })}
                                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-bold text-lg focus:bg-white transition-all"
                                                    placeholder="e.g., EKG Technician Specialist"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-6">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 uppercase">Abbreviation (UI Header)</label>
                                                <input 
                                                    type="text" 
                                                    value={currentCert.shortName}
                                                    onChange={(e) => setCurrentCert({ ...currentCert, shortName: e.target.value.toUpperCase() })}
                                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-black text-lg focus:bg-white transition-all"
                                                    placeholder="e.g., EKG"
                                                />
                                            </div>
                                            <div className="p-6 bg-slate-900 rounded-[2rem] text-white flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <button 
                                                        onClick={() => setCurrentCert({...currentCert, isActive: !currentCert.isActive})} 
                                                        className={currentCert.isActive ? 'text-indigo-400' : 'text-slate-600'}
                                                    >
                                                        {currentCert.isActive ? <ToggleRight size={44}/> : <ToggleLeft size={44}/>}
                                                    </button>
                                                    <div>
                                                        <p className="text-xs font-black">Active Registry</p>
                                                        <p className="text-[9px] text-slate-400 tracking-widest">Toggle candidate portal availability</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Module Access Control */}
                                    <div className="space-y-6">
                                        <h4 className="text-[10px] font-black text-slate-400 tracking-[0.3em] flex items-center gap-2 border-b border-slate-100 pb-2 uppercase">
                                            <Layout size={14} className="text-indigo-500" /> Module Access Control
                                        </h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {Object.keys(currentCert.modules || DEFAULT_MODULE_CONFIG).map(m => (
                                                <button 
                                                    key={m}
                                                    onClick={() => toggleModule(m as any)}
                                                    className={`p-4 rounded-2xl border-2 transition-all text-left flex flex-col gap-3 ${currentCert.modules![m as keyof typeof currentCert.modules] ? 'bg-white border-indigo-500 shadow-md ring-4 ring-indigo-500/5' : 'bg-slate-50 border-slate-100 grayscale opacity-60'}`}
                                                >
                                                    <span className="text-[9px] font-black tracking-tight text-slate-800">
                                                        {m.charAt(0).toUpperCase() + m.slice(1).replace(/([A-Z])/g, ' $1')}
                                                    </span>
                                                    <div className="flex justify-between items-center">
                                                        <span className={`text-[7px] font-black ${currentCert.modules![m as keyof typeof currentCert.modules] ? 'text-indigo-600' : 'text-slate-400'}`}>
                                                            {currentCert.modules![m as keyof typeof currentCert.modules] ? 'Enabled' : 'Hidden'}
                                                        </span>
                                                        <div className={`w-2 h-2 rounded-full ${currentCert.modules![m as keyof typeof currentCert.modules] ? 'bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]' : 'bg-slate-300'}`}></div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TAB: CLINICAL & BOARD COUNCIL */}
                            {activeModalTab === 'clinical' && (
                                <div className="space-y-8 animate-slide-up">
                                    {/* Clinical Council Banner */}
                                    <div className="bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 p-8 rounded-[2.5rem] text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border border-indigo-500/20">
                                        <div className="space-y-2">
                                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-[10px] font-black tracking-widest border border-indigo-400/20">
                                                <GraduationCap size={12} /> Tier-3 Clinical Council & Accreditation Matrix
                                            </div>
                                            <h3 className="text-xl font-black tracking-tight">
                                                Doctoral & Master's Educator Governance for {currentCert.shortName || currentCert.name || 'Certification'}
                                            </h3>
                                            <p className="text-xs text-slate-300 font-medium max-w-2xl leading-relaxed">
                                                All AI-synthesized quizzes, study guides, flashcards, skills labs, and math lab scenarios are authored, reviewed, and audited against this clinical authority matrix. Ensures genuine board alignment, zero-tolerance safety checks, and strict scope-of-practice guardrails.
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-3 shrink-0">
                                            <button
                                                onClick={handleAutoPopulateClinical}
                                                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-black tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-indigo-500/25 active:scale-95"
                                                title="Auto-populate clinical matrix using domain defaults"
                                            >
                                                <Wand2 size={14} /> Auto-Bootstrap Council
                                            </button>
                                        </div>
                                    </div>

                                    {/* Form Fields */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* Left Column: Persona & Board Authority */}
                                        <div className="space-y-6">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase flex items-center gap-1.5">
                                                    <GraduationCap size={14} className="text-indigo-600" />
                                                    Lead Clinical Educator Persona & Credentials
                                                </label>
                                                <textarea
                                                    rows={4}
                                                    value={currentCert.clinicalConfig?.educatorPersona || ''}
                                                    onChange={e => updateClinicalConfig('educatorPersona', e.target.value)}
                                                    placeholder="e.g., Doctor of Nursing Practice (DNP), MSN-Ed, RN Clinical Nurse Specialist & State Board Evaluator..."
                                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-bold text-xs text-slate-700 leading-relaxed focus:bg-white focus:ring-4 focus:ring-indigo-500/5 transition-all shadow-inner"
                                                />
                                                <p className="text-[10px] text-slate-400">Specifies the doctoral/master's teaching authority prompt behind every generation.</p>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase flex items-center gap-1.5">
                                                    <ShieldCheck size={14} className="text-indigo-600" />
                                                    Primary Governing Board / Credentialing Commission
                                                </label>
                                                <input
                                                    type="text"
                                                    value={currentCert.clinicalConfig?.governingBoard || ''}
                                                    onChange={e => updateClinicalConfig('governingBoard', e.target.value)}
                                                    placeholder="e.g., Credentia (NNAAP) / Prometric / NCSBN"
                                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-black text-sm text-slate-800 focus:bg-white transition-all shadow-inner"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase flex items-center gap-1.5">
                                                    <Beaker size={14} className="text-indigo-600" />
                                                    Accreditation & Federal Compliance Standards
                                                </label>
                                                <input
                                                    type="text"
                                                    value={currentCert.clinicalConfig?.accreditationStandard || ''}
                                                    onChange={e => updateClinicalConfig('accreditationStandard', e.target.value)}
                                                    placeholder="e.g., CDC, OSHA, Joint Commission (TJC), CMS Title 42 OBRA"
                                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-bold text-xs text-slate-700 focus:bg-white transition-all shadow-inner"
                                                />
                                            </div>
                                        </div>

                                        {/* Right Column: Setting, Subject, and Scope */}
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase flex items-center gap-1.5">
                                                        <Activity size={14} className="text-indigo-600" />
                                                        Subject Terminology
                                                    </label>
                                                    <select
                                                        value={currentCert.clinicalConfig?.subjectRole || 'resident'}
                                                        onChange={e => updateClinicalConfig('subjectRole', e.target.value as any)}
                                                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-black text-xs text-slate-700 focus:bg-white transition-all shadow-inner"
                                                    >
                                                        <option value="resident">Resident (LTC / Skilled Nursing)</option>
                                                        <option value="patient">Patient (Hospital / Clinic / Acute)</option>
                                                        <option value="client">Client (Home Health / Community)</option>
                                                    </select>
                                                </div>

                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase flex items-center gap-1.5">
                                                        <Stethoscope size={14} className="text-indigo-600" />
                                                        Clinical Setting
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={currentCert.clinicalConfig?.clinicalSetting || ''}
                                                        onChange={e => updateClinicalConfig('clinicalSetting', e.target.value)}
                                                        placeholder="e.g., Skilled Nursing, Subacute Rehabilitation"
                                                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-bold text-xs text-slate-700 focus:bg-white transition-all shadow-inner"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase flex items-center gap-1.5">
                                                    <Shield size={14} className="text-rose-500" />
                                                    Scope of Practice Mandates (Legal Boundaries)
                                                </label>
                                                <textarea
                                                    rows={3}
                                                    value={currentCert.clinicalConfig?.scopeOfPracticeGuidelines || ''}
                                                    onChange={e => updateClinicalConfig('scopeOfPracticeGuidelines', e.target.value)}
                                                    placeholder="Mandated delegated boundaries; e.g., no unauthorized medication administration, prompt escalation to supervising RN..."
                                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-bold text-xs text-slate-700 leading-relaxed focus:bg-white transition-all shadow-inner"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase flex items-center gap-1.5">
                                                    <AlertTriangle size={14} className="text-amber-500" />
                                                    Zero-Tolerance Safety Criticals
                                                </label>
                                                <textarea
                                                    rows={3}
                                                    value={currentCert.clinicalConfig?.safetyGuidelines || ''}
                                                    onChange={e => updateClinicalConfig('safetyGuidelines', e.target.value)}
                                                    placeholder="Non-negotiable patient safety checkpoints; e.g., call light in reach, bed locked in lowest position, fall risk precautions..."
                                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-bold text-xs text-slate-700 leading-relaxed focus:bg-white transition-all shadow-inner"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Live Interactive Clinical Reviewer Audit Testbed */}
                                    <div className="bg-slate-50 border border-slate-200 rounded-[2.5rem] p-6 md:p-8 space-y-6">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <div className="space-y-1">
                                                <h4 className="text-sm font-black text-slate-900 tracking-tight flex items-center gap-2">
                                                    <FileCheck2 size={18} className="text-indigo-600" />
                                                    Live Clinical Auditor Verification Testbed
                                                </h4>
                                                <p className="text-xs text-slate-500 font-medium">
                                                    Simulates the 3-Tier Clinical Council audit on a sample clinical scenario using this certification's exact matrix.
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleRunClinicalAuditTest}
                                                disabled={isTestingClinicalAudit}
                                                className="px-5 py-3 bg-slate-900 hover:bg-black text-white rounded-2xl text-xs font-black tracking-widest transition-all flex items-center gap-2 shrink-0 disabled:opacity-50 shadow-md active:scale-95"
                                            >
                                                {isTestingClinicalAudit ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin text-indigo-400" />
                                                        Auditing Clinical Item...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Sparkles size={16} className="text-indigo-400" />
                                                        Run Clinical Council Audit Test
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {clinicalAuditTestResult && (
                                            <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 animate-slide-up shadow-sm">
                                                <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-100">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center font-black text-lg">
                                                            {clinicalAuditTestResult.overallHealthScore}%
                                                        </div>
                                                        <div>
                                                            <div className="text-xs font-black text-slate-900 flex items-center gap-1.5">
                                                                Clinical Rigor Score
                                                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-[9px] font-bold">
                                                                    Validated
                                                                </span>
                                                            </div>
                                                            <div className="text-[10px] text-slate-500 font-mono">
                                                                Bloom's Level: {clinicalAuditTestResult.bloomsTaxonomyLevel || 'Applying'}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap gap-2">
                                                        <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-[10px] font-bold">
                                                            Accuracy: {clinicalAuditTestResult.clinicalAccuracyScore}/10
                                                        </span>
                                                        <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-[10px] font-bold">
                                                            Distractor Rigor: {clinicalAuditTestResult.distractorRigorScore}/10
                                                        </span>
                                                        <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-[10px] font-bold">
                                                            Board Alignment: {clinicalAuditTestResult.boardAlignmentScore}/10
                                                        </span>
                                                        <span className={`px-3 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 ${clinicalAuditTestResult.scopeOfPracticePassed ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                                            <CheckCheck size={12} /> Scope: {clinicalAuditTestResult.scopeOfPracticePassed ? 'Passed' : 'Flagged'}
                                                        </span>
                                                        <span className={`px-3 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 ${clinicalAuditTestResult.safetyCriticalPassed ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                                            <ShieldCheck size={12} /> Safety: {clinicalAuditTestResult.safetyCriticalPassed ? 'Passed' : 'Flagged'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="space-y-2">
                                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                        Auditor Persona & Critique:
                                                    </div>
                                                    <p className="text-xs text-slate-700 font-medium leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                                                        {clinicalAuditTestResult.critiqueAndRecommendations || clinicalAuditTestResult.editorialFeedback || 'Content meets doctoral educator standards and board credentialing criteria.'}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* TAB 2: GUEST MODE & ROUTE CONTROLLER */}
                            {activeModalTab === 'guest' && (
                                <div className="space-y-8 animate-slide-up">
                                    {/* Top Banner with Master Switch */}
                                    <div className="bg-gradient-to-br from-indigo-900 to-slate-900 p-8 rounded-[2.5rem] text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                                        <div className="space-y-2">
                                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-[10px] font-black tracking-widest border border-indigo-400/20">
                                                <Shield size={12} /> Guest Exploration Architecture
                                            </div>
                                            <h3 className="text-xl font-black tracking-tight">
                                                Dynamic Route Permissions for {currentCert.shortName || currentCert.name || 'Certification'}
                                            </h3>
                                            <p className="text-xs text-slate-300 font-medium max-w-2xl leading-relaxed">
                                                Choose exactly which routes unauthenticated visitors can explore without logging in. All routes are controlled dynamically by this certification configuration without requiring any code changes.
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-4 bg-white/10 p-4 rounded-2xl border border-white/10 shrink-0">
                                            <div>
                                                <div className="text-[10px] font-black uppercase tracking-wider text-indigo-300">
                                                    Guest Mode Status
                                                </div>
                                                <div className="text-sm font-black">
                                                    {currentCert.isGuestTrialEnabled !== false ? 'Enabled' : 'Disabled'}
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => setCurrentCert({ ...currentCert, isGuestTrialEnabled: currentCert.isGuestTrialEnabled === false ? true : false })}
                                                className={currentCert.isGuestTrialEnabled !== false ? 'text-indigo-400' : 'text-slate-500'}
                                            >
                                                {currentCert.isGuestTrialEnabled !== false ? <ToggleRight size={44} /> : <ToggleLeft size={44} />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Presets & Batch Toolbar */}
                                    <div className="p-6 bg-slate-50 rounded-3xl border border-slate-200/80 space-y-4">
                                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                            <div>
                                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Quick Route Presets</h4>
                                                <p className="text-[10px] text-slate-500 font-medium">Quickly configure standard accessibility profiles</p>
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <button 
                                                    onClick={() => handleApplyPreset('all')}
                                                    className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-[10px] font-black transition-all flex items-center gap-1.5"
                                                >
                                                    <Sparkles size={12} /> Open All Routes
                                                </button>
                                                <button 
                                                    onClick={() => handleApplyPreset('core')}
                                                    className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl text-[10px] font-black transition-all flex items-center gap-1.5"
                                                >
                                                    <Zap size={12} /> Core Practice Only
                                                </button>
                                                <button 
                                                    onClick={() => handleApplyPreset('knowledge')}
                                                    className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl text-[10px] font-black transition-all flex items-center gap-1.5"
                                                >
                                                    <BookOpen size={12} /> Learning Hub Only
                                                </button>
                                                <button 
                                                    onClick={() => handleApplyPreset('none')}
                                                    className="px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-xl text-[10px] font-black transition-all flex items-center gap-1.5"
                                                >
                                                    <Lock size={12} /> Restrict All (Require Login)
                                                </button>
                                            </div>
                                        </div>

                                        <div className="pt-3 border-t border-slate-200/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500">
                                                <span>Active Allowed Routes:</span>
                                                <span className="px-2 py-0.5 bg-indigo-600 text-white rounded-md font-mono font-black">
                                                    {(currentCert.guestAllowedRoutes || []).length} / {ALL_AVAILABLE_CERT_ROUTES.length}
                                                </span>
                                            </div>
                                            <button 
                                                type="button"
                                                disabled={isBatchApplying}
                                                onClick={handleBatchApplyGuestPolicy}
                                                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-[10px] font-black tracking-wider transition-all flex items-center gap-2 shadow-sm disabled:opacity-50"
                                            >
                                                {isBatchApplying ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} />}
                                                Apply This Route Policy to All Certifications
                                            </button>
                                        </div>
                                    </div>

                                    {/* Search & Filter Bar */}
                                    <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4">
                                        <div className="flex items-center gap-2 overflow-x-auto pb-1">
                                            {['all', 'Practice & Prep', 'Knowledge & Audio', 'Interactive Labs', 'Analytics & Community'].map(cat => (
                                                <button 
                                                    key={cat}
                                                    onClick={() => setRouteCategoryFilter(cat)}
                                                    className={`px-3 py-1.5 rounded-xl text-[10px] font-black whitespace-nowrap transition-all ${
                                                        routeCategoryFilter === cat 
                                                            ? 'bg-indigo-600 text-white shadow-sm' 
                                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                    }`}
                                                >
                                                    {cat === 'all' ? 'All Categories' : cat}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="relative">
                                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                            <input 
                                                type="text" 
                                                value={routeSearchQuery}
                                                onChange={e => setRouteSearchQuery(e.target.value)}
                                                placeholder="Search routes..."
                                                className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold w-full md:w-60 focus:bg-white outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>
                                    </div>

                                    {/* Available Routes Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {filteredRoutes.map(route => {
                                            const IconComp = route.icon;
                                            const allowedRoutes = currentCert.guestAllowedRoutes || ['flashcards', 'practice', 'study-quiz'];
                                            const isAllowed = allowedRoutes.includes(route.id);
                                            const isGuestActiveOverall = currentCert.isGuestTrialEnabled !== false;
                                            const effectivelyOpen = isGuestActiveOverall && isAllowed;

                                            return (
                                                <div 
                                                    key={route.id}
                                                    onClick={() => handleToggleGuestRoute(route.id)}
                                                    className={`p-5 rounded-3xl border-2 cursor-pointer transition-all flex flex-col justify-between space-y-4 group ${
                                                        effectivelyOpen 
                                                            ? 'bg-indigo-50/20 border-indigo-500 shadow-md ring-4 ring-indigo-500/5' 
                                                            : isAllowed
                                                                ? 'bg-slate-50/80 border-slate-300 opacity-70'
                                                                : 'bg-white border-slate-200 hover:border-slate-300'
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex items-start gap-3.5">
                                                            <div className={`p-3 rounded-2xl shrink-0 transition-colors ${
                                                                effectivelyOpen ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-500'
                                                            }`}>
                                                                <IconComp size={20} />
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <h5 className="font-black text-slate-900 text-sm">{route.name}</h5>
                                                                    <span className="text-[8px] font-black uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                                                                        {route.category}
                                                                    </span>
                                                                </div>
                                                                <p className="text-[10px] text-slate-500 font-medium mt-1 leading-relaxed">
                                                                    {route.description}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {/* Toggle Switch */}
                                                        <div className="shrink-0 pt-0.5">
                                                            <button 
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); handleToggleGuestRoute(route.id); }}
                                                                className={isAllowed ? 'text-indigo-600' : 'text-slate-300'}
                                                            >
                                                                {isAllowed ? <ToggleRight size={38} /> : <ToggleLeft size={38} />}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center justify-between pt-3 border-t border-slate-100 text-[9px] font-mono">
                                                        <span className="text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md font-bold">
                                                            {route.urlPattern(currentCert.id || 'cert')}
                                                        </span>
                                                        <span className={`font-black uppercase tracking-wider ${
                                                            effectivelyOpen ? 'text-indigo-600 flex items-center gap-1' : 'text-slate-400'
                                                        }`}>
                                                            {effectivelyOpen ? (
                                                                <>
                                                                    <Check size={10} /> Guest Accessible
                                                                </>
                                                            ) : isAllowed && !isGuestActiveOverall ? (
                                                                'Allowed (Master Guest Off)'
                                                            ) : (
                                                                'Login Required'
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Custom Route URL Patterns */}
                                    <div className="p-6 bg-slate-50 rounded-3xl border border-slate-200 space-y-4">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Custom Guest Route Patterns</h4>
                                                <p className="text-[10px] text-slate-500 font-medium">Add additional custom route paths to allow for guest exploration (e.g., custom quiz or demo pages)</p>
                                            </div>
                                        </div>

                                        <div className="flex gap-2">
                                            <input 
                                                type="text" 
                                                value={newCustomRouteInput}
                                                onChange={e => setNewCustomRouteInput(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddCustomRoute())}
                                                placeholder="e.g. mini-quiz, special-demo, state-review"
                                                className="flex-1 p-3 bg-white border border-slate-200 rounded-xl text-xs font-mono font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                            <button 
                                                type="button"
                                                onClick={handleAddCustomRoute}
                                                className="px-5 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black hover:bg-indigo-700 transition-all flex items-center gap-1.5"
                                            >
                                                <Plus size={14} /> Add Pattern
                                            </button>
                                        </div>

                                        <div className="flex flex-wrap gap-2 pt-1">
                                            {(currentCert.customGuestRoutes || []).map(pattern => (
                                                <span key={pattern} className="px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 rounded-xl text-xs font-mono font-bold flex items-center gap-2 shadow-sm">
                                                    /{currentCert.id || 'cna'}/{pattern}
                                                    <button 
                                                        type="button"
                                                        onClick={() => handleRemoveCustomRoute(pattern)}
                                                        className="p-0.5 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </span>
                                            ))}
                                            {(!currentCert.customGuestRoutes || currentCert.customGuestRoutes.length === 0) && (
                                                <span className="text-[10px] text-slate-400 italic">No custom route patterns defined.</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Free Item Limit Override for this Cert */}
                                    <div className="p-6 bg-slate-50 rounded-3xl border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                        <div>
                                            <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Guest Exploration Limit (Per Candidate Session)</h4>
                                            <p className="text-[10px] text-slate-500 font-medium">Number of items (flashcards/questions) an unauthenticated guest can view before triggering the registration modal.</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <input 
                                                type="number" 
                                                min="5" 
                                                max="500"
                                                value={currentCert.guestQuestionLimit || 50}
                                                onChange={e => setCurrentCert({ ...currentCert, guestQuestionLimit: parseInt(e.target.value) || 50 })}
                                                className="w-24 p-3 bg-white border border-slate-200 rounded-xl text-center font-black text-sm text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                            <span className="text-xs font-bold text-slate-600">items</span>
                                        </div>
                                    </div>

                                    {/* Free Full Practice Exam Limit Override for this Cert */}
                                    <div className="p-6 bg-slate-50 rounded-3xl border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Guest Full Practice Exam Limit (State Sets)</h4>
                                                <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200">
                                                    {(currentCert.guestExamLimit ?? 1) === 0 ? 'Locked (0)' : `${currentCert.guestExamLimit ?? 1} Free ${(currentCert.guestExamLimit ?? 1) === 1 ? 'Test' : 'Tests'}`}
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-slate-500 font-medium mt-0.5">Number of complete state-standard mock exams unauthenticated guests can attempt (Default: 1 exam). Set to 0 to require sign-up for all mock exams.</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <input 
                                                type="number" 
                                                min="0" 
                                                max="50"
                                                value={currentCert.guestExamLimit !== undefined ? currentCert.guestExamLimit : 1}
                                                onChange={e => setCurrentCert({ ...currentCert, guestExamLimit: Math.max(0, parseInt(e.target.value) || 0) })}
                                                className="w-24 p-3 bg-white border border-slate-200 rounded-xl text-center font-black text-sm text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                            <span className="text-xs font-bold text-slate-600">exams</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TAB 3: GATEWAY DISPLAY */}
                            {activeModalTab === 'gateway' && (
                                <div className="space-y-8 animate-slide-up text-slate-900">
                                    <div className="bg-gradient-to-r from-indigo-50 to-indigo-100/50 p-6 rounded-3xl border border-indigo-100/30 flex items-start gap-4">
                                        <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-lg mt-0.5"><Globe size={20} /></div>
                                        <div>
                                            <h4 className="font-black text-slate-900 text-base">One-Click Gateway Display Controller</h4>
                                            <p className="text-xs text-slate-500 mt-1 font-semibold leading-relaxed">
                                                This certification is fully synchronized with your home gateway portal. Manage visibility, release status, descriptions, and categories directly from this dashboard.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* Display Toggles */}
                                        <div className="space-y-6">
                                            <div className="p-6 bg-slate-50 border border-slate-200 rounded-3xl space-y-6">
                                                <h5 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Visibility Controls</h5>
                                                
                                                <div className="flex items-center justify-between border-b border-slate-100 pb-5">
                                                    <div>
                                                        <p className="text-xs font-black text-slate-800">Show on Home Gateway Page</p>
                                                        <p className="text-[10px] text-slate-400 font-semibold mt-0.5">Toggle card visibility on the portal index</p>
                                                    </div>
                                                    <button 
                                                        onClick={() => setCurrentCert({ ...currentCert!, showOnGateway: !currentCert?.showOnGateway })} 
                                                        className={currentCert?.showOnGateway ? 'text-indigo-600' : 'text-slate-400'}
                                                    >
                                                        {currentCert?.showOnGateway ? <ToggleRight size={44}/> : <ToggleLeft size={44}/>}
                                                    </button>
                                                </div>

                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-xs font-black text-slate-800">Highlight as "Upcoming" Program</p>
                                                        <p className="text-[10px] text-slate-400 font-semibold mt-0.5 font-sans">Show as locked with a "Coming Soon" label</p>
                                                    </div>
                                                    <button 
                                                        onClick={() => setCurrentCert({ ...currentCert!, isUpcoming: !currentCert?.isUpcoming })} 
                                                        className={currentCert?.isUpcoming ? 'text-indigo-600' : 'text-slate-400'}
                                                    >
                                                        {currentCert?.isUpcoming ? <ToggleRight size={44}/> : <ToggleLeft size={44}/>}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="p-6 bg-slate-50 border border-slate-200 rounded-3xl">
                                                <label className="text-[10px] font-black text-slate-400 tracking-[0.2em] uppercase block mb-3">Portal Placement Grid</label>
                                                <select
                                                    value={currentCert?.gatewayCategory || 'Nursing'}
                                                    onChange={(e) => setCurrentCert({ ...currentCert!, gatewayCategory: e.target.value })}
                                                    className="w-full p-4 bg-white border border-slate-200 rounded-2xl outline-none font-black text-xs text-slate-800 focus:ring-2 focus:ring-indigo-500 transition-all cursor-pointer"
                                                >
                                                    <option value="Nursing">Nursing & Patient Care</option>
                                                    <option value="AlliedHealth">Medical Tech & Clinical Labs (Allied Health)</option>
                                                </select>
                                                <span className="text-[10px] text-slate-400 font-semibold mt-2.5 block px-1">Grouping determines which catalog grid section the certification card appears in.</span>
                                            </div>
                                        </div>

                                        {/* Card Copy Description */}
                                        <div className="space-y-6">
                                            <div className="p-6 bg-white border border-slate-200 rounded-3xl space-y-4">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-[10px] font-black text-slate-400 tracking-[0.2em] uppercase">Portal Card Copy Description</label>
                                                    <button
                                                        onClick={() => {
                                                            const preset = `Comprehensive preparation study guides, smart flashcard sets, and dynamic practice board exams calibrated for state-certified ${currentCert?.name || 'healthcare'} registries.`;
                                                            setCurrentCert({ ...currentCert!, gatewayDescription: preset });
                                                        }}
                                                        className="text-[9px] font-black text-indigo-600 flex items-center gap-1 hover:underline uppercase tracking-wider"
                                                    >
                                                        <Sparkles size={11} /> Load Card Preset
                                                    </button>
                                                </div>
                                                <textarea
                                                    value={currentCert?.gatewayDescription || ''}
                                                    onChange={(e) => setCurrentCert({ ...currentCert!, gatewayDescription: e.target.value })}
                                                    className="w-full h-32 p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-bold text-xs text-slate-700 focus:bg-white transition-all focus:ring-2 focus:ring-indigo-500 resize-none"
                                                    placeholder="e.g. Master the vital clinical skills, theoretical competencies, and patient-care safety checklists required to excel in this board exam."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* High-Class Program Cover Image Section */}
                                    <div className="p-6 bg-slate-50 border border-slate-200 rounded-3xl space-y-5">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/80 pb-4">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2.5 bg-white text-indigo-600 rounded-2xl border border-slate-200 shadow-xs">
                                                    <ImageIcon size={20} />
                                                </div>
                                                <div>
                                                    <h5 className="text-xs font-black text-slate-900 uppercase tracking-wide">Gateway Program Cover Photo</h5>
                                                    <p className="text-[10px] text-slate-400 font-semibold">High-resolution photography displayed on the public gateway portal card</p>
                                                </div>
                                            </div>
                                            {currentCert?.gatewayImageUrl && (
                                                <button
                                                    type="button"
                                                    onClick={() => setCurrentCert({ ...currentCert!, gatewayImageUrl: '' })}
                                                    className="text-[10px] font-black text-rose-600 hover:text-rose-700 hover:underline uppercase tracking-wider self-start sm:self-auto cursor-pointer"
                                                >
                                                    Reset to Default Image
                                                </button>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                                            {/* URL Input & Presets */}
                                            <div className="lg:col-span-7 space-y-4">
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 tracking-[0.15em] uppercase block mb-2">Image URL (Hosted / CDN / Unsplash)</label>
                                                    <input
                                                        type="text"
                                                        value={currentCert?.gatewayImageUrl || ''}
                                                        onChange={(e) => setCurrentCert({ ...currentCert!, gatewayImageUrl: e.target.value })}
                                                        placeholder="https://images.unsplash.com/photo-... or https://static.wixstatic.com/..."
                                                        className="w-full p-4 bg-white border border-slate-200 rounded-2xl outline-none font-mono text-xs text-slate-800 focus:ring-2 focus:ring-indigo-500 transition-all"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 tracking-[0.15em] uppercase block mb-2">Curated Healthcare Presets</label>
                                                    <div className="flex flex-wrap gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => setCurrentCert({ ...currentCert!, gatewayImageUrl: 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&w=800&q=80' })}
                                                            className="px-3 py-1.5 bg-white hover:bg-indigo-50 hover:text-indigo-600 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-600 transition-all cursor-pointer active:scale-95 flex items-center gap-1.5"
                                                        >
                                                            <span>🩺 CNA Bedside Care</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setCurrentCert({ ...currentCert!, gatewayImageUrl: 'https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=800&q=80' })}
                                                            className="px-3 py-1.5 bg-white hover:bg-indigo-50 hover:text-indigo-600 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-600 transition-all cursor-pointer active:scale-95 flex items-center gap-1.5"
                                                        >
                                                            <span>🧪 Phlebotomy & Lab</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setCurrentCert({ ...currentCert!, gatewayImageUrl: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=800&q=80' })}
                                                            className="px-3 py-1.5 bg-white hover:bg-indigo-50 hover:text-indigo-600 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-600 transition-all cursor-pointer active:scale-95 flex items-center gap-1.5"
                                                        >
                                                            <span>📋 Medical Assistant</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setCurrentCert({ ...currentCert!, gatewayImageUrl: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=800&q=80' })}
                                                            className="px-3 py-1.5 bg-white hover:bg-indigo-50 hover:text-indigo-600 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-600 transition-all cursor-pointer active:scale-95 flex items-center gap-1.5"
                                                        >
                                                            <span>💊 Pharmacy Tech</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setCurrentCert({ ...currentCert!, gatewayImageUrl: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&w=800&q=80' })}
                                                            className="px-3 py-1.5 bg-white hover:bg-indigo-50 hover:text-indigo-600 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-600 transition-all cursor-pointer active:scale-95 flex items-center gap-1.5"
                                                        >
                                                            <span>🏥 Patient Care (PCT)</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Live Gateway Card Preview */}
                                            <div className="lg:col-span-5">
                                                <label className="text-[10px] font-black text-slate-400 tracking-[0.15em] uppercase block mb-2">Live Gateway Card Preview</label>
                                                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm group">
                                                    <div className="relative h-32 w-full bg-slate-900 overflow-hidden">
                                                        <img 
                                                            src={currentCert?.gatewayImageUrl || 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&w=800&q=80'} 
                                                            alt="Preview" 
                                                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&w=800&q=80';
                                                            }}
                                                        />
                                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-black/30"></div>
                                                        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-wider bg-white/90 text-slate-900 backdrop-blur-xs shadow-xs">
                                                            {currentCert?.gatewayCategory === 'AlliedHealth' ? 'Medical Tech' : 'Nursing & Care'}
                                                        </span>
                                                        <span className={`absolute top-3 right-3 px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-wider ${
                                                            currentCert?.isUpcoming 
                                                                ? 'bg-amber-500 text-white' 
                                                                : 'bg-emerald-600 text-white'
                                                        }`}>
                                                            {currentCert?.isUpcoming ? 'Coming Soon' : 'Operational'}
                                                        </span>
                                                    </div>
                                                    <div className="p-4 space-y-2">
                                                        <h6 className="font-black text-sm text-slate-900 leading-tight">
                                                            {currentCert?.name || 'Program Title'} ({currentCert?.shortName || 'CODE'})
                                                        </h6>
                                                        <p className="text-[10px] text-slate-500 line-clamp-2 leading-relaxed">
                                                            {currentCert?.gatewayDescription || 'Complete state board preparation and practice tests.'}
                                                        </p>
                                                        <div className="pt-2">
                                                            <div className="w-full py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest text-center">
                                                                Enter Study Hub
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TAB: SEO & DETAILS PAGE */}
                            {activeModalTab === 'seo_details' && (
                                <div className="space-y-8 animate-slide-up">
                                    {/* 1-Click Launch Header Card */}
                                    <div className="bg-gradient-to-br from-indigo-900 via-slate-900 to-[#0F7D6C] text-white p-6 md:p-8 rounded-[2rem] shadow-xl relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                                            <Sparkles size={160} />
                                        </div>
                                        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                                            <div className="space-y-2 max-w-xl">
                                                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-[10px] font-black uppercase tracking-wider text-emerald-300">
                                                    <Sparkles size={12} />
                                                    1-Click AI Synthesis & Launch Engine
                                                </div>
                                                <h4 className="text-2xl font-black tracking-tight text-white">
                                                    SEO & LLM Gateway Landing Page
                                                </h4>
                                                <p className="text-xs text-slate-300 leading-relaxed">
                                                    Automatically generates a high-converting, non-state-specific educational details page with role explanations, curriculum offerings, exam format, FAQ schema, and instant search engine / LLM crawler indexing.
                                                </p>
                                            </div>

                                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                                                <button
                                                    onClick={handleOneClickGenerateAndLaunchSeoPage}
                                                    disabled={isGeneratingSeoPage}
                                                    className="px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-2xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg hover:shadow-emerald-500/25 transition-all active:scale-95 disabled:opacity-50"
                                                >
                                                    {isGeneratingSeoPage ? (
                                                        <>
                                                            <Loader2 size={16} className="animate-spin" />
                                                            <span>Synthesizing & Deploying...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Wand2 size={16} />
                                                            <span>1-Click Generate & Launch</span>
                                                        </>
                                                    )}
                                                </button>

                                                <a
                                                    href={`/programs/${currentCert.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="px-5 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-2xl font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all border border-white/10"
                                                >
                                                    <Globe size={14} />
                                                    <span>Preview Live Page</span>
                                                    <ArrowRight size={14} />
                                                </a>
                                            </div>
                                        </div>

                                        {seoGenerateSuccess && (
                                            <div className="mt-6 p-4 bg-emerald-500/20 border border-emerald-400/40 rounded-2xl flex items-center justify-between gap-4 text-emerald-200">
                                                <div className="flex items-center gap-2.5 text-xs font-bold">
                                                    <CheckCircle size={16} className="text-emerald-400 shrink-0" />
                                                    <span>{seoGenerateSuccess}</span>
                                                </div>
                                                <a
                                                    href={`/programs/${currentCert.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs font-black text-white underline hover:no-underline shrink-0"
                                                >
                                                    View Page &rarr;
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    {/* Publication & URL Reference Bar */}
                                    <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={currentCert.gatewaySeo?.isPublished !== false}
                                                    onChange={e => updateGatewaySeo('isPublished', e.target.checked)}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                                            </label>
                                            <div>
                                                <span className="text-xs font-black text-slate-800 uppercase tracking-wider">
                                                    Published Status
                                                </span>
                                                <p className="text-[10px] text-slate-500">
                                                    {currentCert.gatewaySeo?.isPublished !== false ? 'Live and indexed in sitemap.xml & llms.txt' : 'Unpublished (draft only)'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 bg-white px-3.5 py-2 rounded-xl border border-slate-200 text-[11px] font-mono text-slate-600">
                                            <span className="text-slate-400 font-bold">Direct URL:</span>
                                            <span className="font-bold text-indigo-600">/programs/{currentCert.id}</span>
                                            <button
                                                onClick={() => navigator.clipboard.writeText(`https://study.diamondnursingschool.com/programs/${currentCert.id}`)}
                                                className="p-1 hover:text-slate-900 transition-colors"
                                                title="Copy Full URL"
                                            >
                                                <Copy size={13} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Google SERP & LLM Citation Preview */}
                                    <div className="p-6 bg-white border border-slate-200 rounded-2xl space-y-3">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                            <Search size={12} />
                                            Google Search Result & AI Citation Preview
                                        </span>
                                        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 font-sans space-y-1">
                                            <div className="text-[11px] text-slate-500 flex items-center gap-1">
                                                <span>study.diamondnursingschool.com</span>
                                                <span>›</span>
                                                <span>practice-exams</span>
                                                <span>›</span>
                                                <span className="text-slate-700">programs</span>
                                                <span>›</span>
                                                <span className="text-slate-700">{currentCert.id}</span>
                                            </div>
                                            <div className="text-base font-bold text-blue-800 hover:underline cursor-pointer">
                                                {currentCert.gatewaySeo?.pageTitle || `${currentCert.name} (${currentCert.shortName}) Certification Guide & Board Exam Prep`} | Diamond School
                                            </div>
                                            <div className="text-xs text-slate-600 leading-relaxed line-clamp-2">
                                                {currentCert.gatewaySeo?.metaDescription || `Comprehensive guide to the ${currentCert.name} (${currentCert.shortName}) credential. Role overview, daily clinical responsibilities, exam format, FAQs, and free diagnostic practice exams.`}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Metadata & Headlines */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                    SEO Meta Title Tag
                                                </label>
                                                <input
                                                    type="text"
                                                    value={currentCert.gatewaySeo?.pageTitle || ''}
                                                    onChange={e => updateGatewaySeo('pageTitle', e.target.value)}
                                                    placeholder="e.g. Certified Nursing Assistant (CNA) Certification Guide & Free Practice Test"
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:border-indigo-500"
                                                />
                                            </div>

                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                    Meta Description Tag
                                                </label>
                                                <textarea
                                                    rows={3}
                                                    value={currentCert.gatewaySeo?.metaDescription || ''}
                                                    onChange={e => updateGatewaySeo('metaDescription', e.target.value)}
                                                    placeholder="150-160 character concise search engine snippet..."
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 focus:outline-none focus:border-indigo-500 leading-relaxed"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                    Page H1 Main Headline
                                                </label>
                                                <input
                                                    type="text"
                                                    value={currentCert.gatewaySeo?.heroHeadline || ''}
                                                    onChange={e => updateGatewaySeo('heroHeadline', e.target.value)}
                                                    placeholder="e.g. Certified Nursing Assistant (CNA) Certification Guide"
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:border-indigo-500"
                                                />
                                            </div>

                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                    Page Hero Subheadline
                                                </label>
                                                <textarea
                                                    rows={3}
                                                    value={currentCert.gatewaySeo?.heroSubheadline || ''}
                                                    onChange={e => updateGatewaySeo('heroSubheadline', e.target.value)}
                                                    placeholder="Compelling secondary proposition for prospective healthcare candidates..."
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 focus:outline-none focus:border-indigo-500 leading-relaxed"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Role Overview (Plain English) */}
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                            Generic Role Overview (Assumes User Knows Nothing - Plain English)
                                        </label>
                                        <textarea
                                            rows={4}
                                            value={currentCert.gatewaySeo?.roleOverview || ''}
                                            onChange={e => updateGatewaySeo('roleOverview', e.target.value)}
                                            placeholder="Explain what this certification is, who issues it, why it is needed, and how the credential qualifies professionals..."
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs text-slate-800 focus:outline-none focus:border-indigo-500 leading-relaxed"
                                        />
                                    </div>

                                    {/* Exam Format & Blueprint Summary */}
                                    <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl space-y-4">
                                        <h5 className="font-black text-xs uppercase tracking-wider text-slate-900 flex items-center gap-2">
                                            <Timer size={14} className="text-indigo-600" />
                                            Standard Exam Blueprint Specifications
                                        </h5>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-500 uppercase">Exam Format</label>
                                                <input
                                                    type="text"
                                                    value={currentCert.gatewaySeo?.examSummary?.formatType || ''}
                                                    onChange={e => updateExamSummary('formatType', e.target.value)}
                                                    placeholder="e.g. CBT Multiple-Choice"
                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-500 uppercase">Total Questions</label>
                                                <input
                                                    type="text"
                                                    value={currentCert.gatewaySeo?.examSummary?.totalQuestions || ''}
                                                    onChange={e => updateExamSummary('totalQuestions', e.target.value)}
                                                    placeholder="e.g. 70 Questions"
                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-500 uppercase">Time Allotted</label>
                                                <input
                                                    type="text"
                                                    value={currentCert.gatewaySeo?.examSummary?.timeAllotted || ''}
                                                    onChange={e => updateExamSummary('timeAllotted', e.target.value)}
                                                    placeholder="e.g. 90 Minutes"
                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-500 uppercase">Passing Score</label>
                                                <input
                                                    type="text"
                                                    value={currentCert.gatewaySeo?.examSummary?.passingScore || ''}
                                                    onChange={e => updateExamSummary('passingScore', e.target.value)}
                                                    placeholder="e.g. 70% or 75%"
                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] font-bold text-slate-500 uppercase">Clinical Skills Evaluation Description</label>
                                            <textarea
                                                rows={2}
                                                value={currentCert.gatewaySeo?.examSummary?.skillsTestDescription || ''}
                                                onChange={e => updateExamSummary('skillsTestDescription', e.target.value)}
                                                placeholder="Explain how hands-on skills or simulation assessments are graded..."
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-700"
                                            />
                                        </div>
                                    </div>

                                    {/* Dynamic FAQs Section */}
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h5 className="font-black text-sm text-slate-900 flex items-center gap-2">
                                                    <HelpCircle size={16} className="text-indigo-600" />
                                                    Program FAQs (Indexed as JSON-LD FAQPage Schema)
                                                </h5>
                                                <p className="text-[10px] text-slate-500">
                                                    These questions are rendered on the public details page and indexed directly into Google Search snippets and AI chat responses.
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={addFaqItem}
                                                className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-black flex items-center gap-1.5 transition-colors"
                                            >
                                                <Plus size={14} />
                                                <span>Add FAQ</span>
                                            </button>
                                        </div>

                                        <div className="space-y-3">
                                            {(currentCert.gatewaySeo?.faqs || []).map((faq, idx) => (
                                                <div key={idx} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2 group">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <input
                                                            type="text"
                                                            value={faq.question}
                                                            onChange={e => updateFaqItem(idx, 'question', e.target.value)}
                                                            placeholder="Question e.g. How long is the certificate valid for?"
                                                            className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => removeFaqItem(idx)}
                                                            className="p-2 text-slate-400 hover:text-rose-500 transition-colors"
                                                            title="Delete FAQ item"
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>
                                                    </div>
                                                    <textarea
                                                        rows={2}
                                                        value={faq.answer}
                                                        onChange={e => updateFaqItem(idx, 'answer', e.target.value)}
                                                        placeholder="Clear, authoritative answer..."
                                                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-700 leading-relaxed"
                                                    />
                                                </div>
                                            ))}
                                            {(!currentCert.gatewaySeo?.faqs || currentCert.gatewaySeo.faqs.length === 0) && (
                                                <div className="p-8 text-center border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 text-xs">
                                                    No FAQs currently configured. Click "1-Click Generate & Launch" above to generate with AI or "Add FAQ" to add manually.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TAB 4: CATEGORIES */}
                            {activeModalTab === 'categories' && (
                                <div className="space-y-8 animate-slide-up">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h4 className="text-xl font-black text-slate-900 tracking-tight">Question Bank Domains</h4>
                                            <p className="text-xs text-slate-400 font-medium">Valid categories for this exam type.</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button 
                                                onClick={handleAiSuggestCategories}
                                                disabled={isSuggestingCategories}
                                                className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-black text-[10px] tracking-widest flex items-center gap-2 shadow-lg hover:bg-indigo-500 transition-all active:scale-95 disabled:opacity-50"
                                            >
                                                {isSuggestingCategories ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
                                                AI Suggest Categories
                                            </button>
                                            <button onClick={addCategory} className="px-6 py-3 bg-slate-900 text-white rounded-xl font-black text-[10px] tracking-widest flex items-center gap-2 shadow-lg hover:bg-black transition-all active:scale-95">
                                                <Plus size={14}/> Add Manually
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {(currentCert.categories || []).map(cat => (
                                            <div key={cat} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between group hover:border-indigo-400 transition-all">
                                                <span className="font-black text-[10px] text-slate-800 truncate pr-4">{cat}</span>
                                                <button onClick={() => removeCategory(cat)} className="p-2 text-slate-300 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"><Trash2 size={14}/></button>
                                            </div>
                                        ))}
                                        {(currentCert.categories || []).length === 0 && (
                                            <div className="col-span-full py-12 text-center opacity-30 border-4 border-dashed border-slate-100 rounded-3xl">
                                                <ListTodo size={48} className="mx-auto mb-4" />
                                                <p className="font-black text-xs">No Categories Defined</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* TAB 5: AI STRATEGY */}
                            {activeModalTab === 'strategy' && (
                                <div className="space-y-10 animate-fade-in">
                                    {!aiStrategy && !isBlueprinting && (
                                        <div className="flex flex-col items-center justify-center py-20 text-center gap-6">
                                            <div className="w-20 h-20 bg-indigo-50 rounded-3xl flex items-center justify-center text-indigo-600 shadow-xl border border-indigo-100">
                                                <Brain size={40} />
                                            </div>
                                            <div className="max-w-md">
                                                <h3 className="text-xl font-black tracking-tight mb-2">Architectural Intelligence</h3>
                                                <p className="text-sm text-slate-500 font-medium leading-relaxed">
                                                    Request a structural blueprint for this certification. The AI will suggest categories, module topics, and laboratory procedures.
                                                </p>
                                            </div>
                                            <button 
                                                onClick={handleBlueprint}
                                                className="px-10 py-5 bg-slate-900 text-white rounded-[1.8rem] font-black text-xs tracking-widest flex items-center gap-3 shadow-2xl hover:bg-black transition-all active:scale-95"
                                            >
                                                <Sparkles size={20} className="text-indigo-400" /> Start AI Blueprinting
                                            </button>
                                        </div>
                                    )}

                                    {isBlueprinting && (
                                        <div className="flex flex-col items-center justify-center py-32 gap-6 opacity-40 animate-pulse">
                                            <Loader2 size={64} className="animate-spin text-indigo-600" />
                                            <p className="font-black text-[10px] tracking-[0.4em]">Calibrating Strategy Board...</p>
                                        </div>
                                    )}

                                    {aiStrategy && (
                                        <div className="space-y-12 pb-10 animate-slide-up">
                                            <div className="p-8 bg-indigo-950 rounded-[2.5rem] text-white relative overflow-hidden shadow-2xl group border border-white/10">
                                                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000"><Wand2 size={120}/></div>
                                                <div className="relative z-10">
                                                    <div className="flex items-center gap-3 mb-6">
                                                        <div className="p-2 bg-white/10 rounded-xl border border-white/20"><Sparkles size={20} className="text-indigo-400" /></div>
                                                        <h4 className="text-2xl font-black tracking-tight leading-none">Blueprint Synthesized</h4>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                        <div className="p-5 bg-white/5 rounded-2xl border border-white/10 space-y-3">
                                                            <div className="flex items-center gap-2 text-indigo-400">
                                                                <ListChecks size={18} />
                                                                <span className="text-[10px] font-black tracking-widest">Quiz Strategy</span>
                                                            </div>
                                                            <p className="text-sm font-bold text-indigo-100 leading-relaxed">"{aiStrategy.quizFormat}"</p>
                                                        </div>
                                                        <div className="p-5 bg-white/5 rounded-2xl border border-white/10 space-y-3">
                                                            <div className="flex items-center gap-2 text-indigo-400">
                                                                <Settings size={18} />
                                                                <span className="text-[10px] font-black tracking-widest">Recommended Icon</span>
                                                            </div>
                                                            <div className="flex items-center gap-4">
                                                                <span className="px-4 py-2 bg-white/10 rounded-xl font-black text-sm tracking-tighter">{aiStrategy.recommendedIcon}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-3 px-1">
                                                        <Layout size={20} className="text-indigo-600" />
                                                        <h4 className="font-black text-sm tracking-widest">Item Bank Schema</h4>
                                                    </div>
                                                    <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner grid grid-cols-1 gap-2">
                                                        {(aiStrategy.categories || []).map((cat: string, i: number) => (
                                                            <div key={i} className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-200 group hover:border-indigo-400 transition-all">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-5 h-5 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-[9px]">{i+1}</div>
                                                                    <span className="text-[10px] font-black text-slate-700">{cat}</span>
                                                                </div>
                                                                <button 
                                                                    onClick={() => {
                                                                        const existing = currentCert.categories || [];
                                                                        if (!existing.includes(cat)) {
                                                                            setCurrentCert({ ...currentCert, categories: [...existing, cat] });
                                                                        }
                                                                    }}
                                                                    className="p-1.5 bg-slate-50 text-slate-400 rounded-lg hover:bg-emerald-50 hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition-all"
                                                                ><Plus size={14}/></button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-3 px-1">
                                                        <BookOpen size={20} className="text-indigo-600" />
                                                        <h4 className="font-black text-sm tracking-widest">Theoretical Modules</h4>
                                                    </div>
                                                    <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner grid grid-cols-1 gap-2">
                                                        {(aiStrategy.studyTopics || []).map((topic: string, i: number) => (
                                                            <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200 group hover:border-indigo-400 transition-all">
                                                                <div className="w-5 h-5 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-[9px]">{i+1}</div>
                                                                <span className="text-[10px] font-black text-slate-700">{topic}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-3 px-1">
                                                        <Stethoscope size={20} className="text-emerald-600" />
                                                        <h4 className="font-black text-sm tracking-widest">Laboratory Procedures</h4>
                                                    </div>
                                                    <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner grid grid-cols-1 gap-2">
                                                        {(aiStrategy.labProcedures || []).map((proc: string, i: number) => (
                                                            <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200 group hover:border-emerald-400 transition-all">
                                                                <div className="w-5 h-5 rounded bg-emerald-50 text-emerald-600 flex items-center justify-center font-black text-[9px]">{i+1}</div>
                                                                <span className="text-[10px] font-black text-slate-700">{proc}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-3 px-1">
                                                        <Beaker size={20} className="text-rose-600" />
                                                        <h4 className="font-black text-sm tracking-widest">Metric Scenarios</h4>
                                                    </div>
                                                    <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner grid grid-cols-1 gap-2">
                                                        {(aiStrategy.mathScenarios || []).map((math: string, i: number) => (
                                                            <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200 group hover:border-rose-400 transition-all">
                                                                <div className="w-5 h-5 rounded bg-rose-50 text-rose-600 flex items-center justify-center font-black text-[9px]">{i+1}</div>
                                                                <span className="text-[10px] font-black text-slate-700">{math}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="p-8 bg-emerald-50 rounded-[2rem] border border-emerald-100 flex items-start gap-5">
                                                <div className="p-3 bg-emerald-500 text-white rounded-2xl shadow-xl"><Info size={24}/></div>
                                                <div>
                                                    <h5 className="font-black text-emerald-900 text-lg tracking-tight mb-1">Blueprint Usage</h5>
                                                    <p className="text-xs text-emerald-800 font-bold leading-relaxed opacity-90">
                                                        This strategy is currently a blueprint. Use these suggested clinical domains and topics to populate the Question Bank, Study Content Manager, and Skills Lab modules for this certification.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="p-8 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-4 shrink-0">
                            <button onClick={() => setIsEditing(false)} className="px-8 py-4 font-black text-[10px] text-slate-400 hover:text-slate-900 transition-colors">Discard Draft</button>
                            <button 
                                onClick={handleSave} 
                                disabled={isSaving}
                                className="px-14 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] tracking-widest shadow-2xl hover:bg-slate-800 transition-all flex items-center gap-3 disabled:opacity-50 border-b-4 border-black"
                            >
                                {isSaving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>} Synchronize Registry
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ToggleLeft = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor" fillOpacity="0.2"/><circle cx="12" cy="12" r="8" fill="white"/></svg>
);

const ToggleRight = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor"/><circle cx="36" cy="12" r="8" fill="white"/></svg>
);
